import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCurrentUser } from "@/shared/auth/useAuth";
import { Role } from "@/shared/types/role";
import { usePendingBalances } from "./use-pending-balances";
import { groupBalancesByVenue, type VenueBalanceGroup } from "./group-balances-by-venue";
import { PayoutReportFilters } from "./PayoutReportFilters";
import { VenueBalanceCard } from "./VenueBalanceCard";
import { PayVenueDialog } from "./PayVenueDialog";
import { LiquidateVenueDialog } from "./LiquidateVenueDialog";
import type { ConnectFilter } from "./constants";
import type { PendingBalanceItem } from "./types";

export function PayoutReportPage() {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  // Money-moving actions are WANDERFIT_ADMIN only (API-FIN-2). FINANCE reads
  // the report but never sees Pay. The API is the real gate; this only hides
  // what the operator can't do.
  const isAdmin = currentUser?.role === Role.WANDERFIT_ADMIN;
  // FINANCE reads the report but the money-moving actions are ADMIN-only, so the
  // Pay/Liquidate affordances simply don't render for them. Surface *why* with a
  // read-only banner (plan §2) so a new finance operator isn't left guessing.
  const isReadOnly = currentUser?.role === Role.WANDERFIT_FINANCE;

  const [connect, setConnect] = useState<ConnectFilter>("all");
  const [olderThanMonths, setOlderThanMonths] = useState<number | null>(null);
  const [payTarget, setPayTarget] = useState<VenueBalanceGroup | null>(null);
  const [liquidateTarget, setLiquidateTarget] = useState<PendingBalanceItem | null>(null);

  const { data, isLoading, isFetching, isError } = usePendingBalances({
    connect,
    olderThanMonths,
  });

  const groups = useMemo(() => groupBalancesByVenue(data?.items ?? []), [data?.items]);
  const hasFilters = connect !== "all" || olderThanMonths !== null;

  const handleReset = () => {
    setConnect("all");
    setOlderThanMonths(null);
  };

  return (
    <div data-testid="payout-report-screen" className="space-y-6">
      <header className="space-y-2">
        <p className="brand-eyebrow">{t("finance.report.eyebrow")}</p>
        <h1 className="text-4xl">{t("finance.report.title")}</h1>
        <p className="font-sans text-base font-normal text-muted-foreground">
          {t("finance.report.subtitle")}
        </p>
      </header>

      {isReadOnly && (
        <p
          data-testid="payout-report-readonly-banner"
          role="status"
          className="rounded-md border border-border bg-muted/40 px-3 py-2 font-sans text-sm text-muted-foreground"
        >
          {t("finance.report.readOnlyBanner")}
        </p>
      )}

      <PayoutReportFilters
        connect={connect}
        olderThanMonths={olderThanMonths}
        onConnectChange={setConnect}
        onOlderThanMonthsChange={setOlderThanMonths}
        onReset={handleReset}
      />

      {isLoading && (
        <p data-testid="payout-report-loading" className="font-sans text-sm text-muted-foreground">
          {t("finance.report.loading")}
        </p>
      )}

      {isError && (
        <div
          data-testid="payout-report-error"
          className="rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-center"
        >
          <p className="font-sans text-sm font-medium text-destructive">
            {t("finance.report.error")}
          </p>
        </div>
      )}

      {!isLoading && !isError && groups.length === 0 && (
        <div
          data-testid="payout-report-empty"
          className="rounded-lg border border-dashed border-border bg-muted/20 p-8 text-center"
        >
          <p className="font-sans text-sm font-medium text-foreground">
            {t("finance.report.emptyTitle")}
          </p>
          <p className="mt-1 font-sans text-sm font-normal text-muted-foreground">
            {hasFilters ? t("finance.report.emptyFiltered") : t("finance.report.emptyBody")}
          </p>
        </div>
      )}

      {!isError && groups.length > 0 && (
        <div
          data-testid="payout-report-list"
          aria-busy={isFetching}
          className={
            isFetching
              ? "grid gap-4 opacity-70 transition-opacity md:grid-cols-2"
              : "grid gap-4 md:grid-cols-2"
          }
        >
          {groups.map((group) => (
            <VenueBalanceCard
              key={group.venueId}
              group={group}
              onPay={isAdmin ? setPayTarget : undefined}
              onLiquidate={isAdmin ? setLiquidateTarget : undefined}
            />
          ))}
        </div>
      )}

      <PayVenueDialog
        group={payTarget}
        open={payTarget !== null}
        onOpenChange={(open) => {
          if (!open) setPayTarget(null);
        }}
      />

      <LiquidateVenueDialog
        item={liquidateTarget}
        open={liquidateTarget !== null}
        onOpenChange={(open) => {
          if (!open) setLiquidateTarget(null);
        }}
      />
    </div>
  );
}
